import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/rn3";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Mail, Send, Eye, AlertCircle, CheckCircle2,
  ToggleLeft, ToggleRight, Save, Zap, ChevronDown, ChevronUp, Layers,
} from "lucide-react";
import { useT } from "@/lib/i18n";

const StatCard = ({ label, value, accent = "", testid }) => (
  <div className="rn3-card" data-testid={testid}>
    <div className={`mt-1 text-3xl font-serif-display ${accent}`}>{value}</div>
    <div className="text-xs uppercase tracking-widest text-[#737373] mt-1">{label}</div>
  </div>
);

const PlaceholderChip = ({ name }) => (
  <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#FDFBF7] border border-[#E5E5E5] text-[#C4A252] font-semibold">
    {"{{"}{name}{"}}"}
  </span>
);

const EmailStepEditor = ({ campaign, email, lang, setLang, onChange, onPreview, onSendTest, busy }) => {
  const t = useT();
  return (
    <div className="border-t border-[#F3EFE9] pt-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs uppercase tracking-widest text-[#0A0A0A]">
          {t("admin.email.step")} {email.step}
          {campaign.kind === "series" && (
            <span className="ml-2 text-[#C4A252]">· {t("admin.email.day")} {email.delay_days}</span>
          )}
        </div>
        <div className="inline-flex border border-[#E5E5E5]" data-testid={`email-lang-toggle-${campaign.key}-${email.step}`}>
          {["en", "es"].map((l) => (
            <button
              key={l}
              onClick={() => setLang(l)}
              className={`px-2.5 py-1 text-[10px] uppercase tracking-widest transition ${lang === l ? "bg-[#0A0A0A] text-white" : "text-[#737373] hover:bg-[#FAFAFA]"}`}
              data-testid={`email-lang-${l}-${campaign.key}-${email.step}`}
            >
              {l}
            </button>
          ))}
        </div>
      </div>

      <label className="rn3-eyebrow block mb-1.5">{t("admin.email.subject")} · {lang === "es" ? "Español" : t("common.english")}</label>
      <input
        value={email[`subject_${lang}`] || ""}
        onChange={(e) => onChange({ ...email, [`subject_${lang}`]: e.target.value })}
        className="rn3-input w-full text-sm mb-3"
        data-testid={`email-subject-${campaign.key}-${email.step}-${lang}`}
      />

      <label className="rn3-eyebrow block mb-1.5">{t("admin.email.body_html")}</label>
      <textarea
        rows={8}
        value={email[`body_${lang}`] || ""}
        onChange={(e) => onChange({ ...email, [`body_${lang}`]: e.target.value })}
        className="rn3-input w-full text-xs font-mono"
        data-testid={`email-body-${campaign.key}-${email.step}-${lang}`}
      />
      <div className="text-[10px] text-[#737373] mt-1">{(email[`body_${lang}`] || "").length} {t("admin.sms.chars")} · {t("admin.email.html_supported")}</div>

      <div className="flex items-center justify-end gap-2 mt-3">
        <button onClick={() => onPreview(email.step, lang)} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`email-preview-${campaign.key}-${email.step}`}>
          <Eye className="w-3 h-3" /> {t("admin.sms.preview")} {lang.toUpperCase()}
        </button>
        <button onClick={() => onSendTest(email.step, lang)} disabled={busy} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1" data-testid={`email-test-${campaign.key}-${email.step}`}>
          <Send className="w-3 h-3" /> {t("admin.sms.send_test")}
        </button>
      </div>
    </div>
  );
};

const EmailCampaignCard = ({ campaign, resendConfigured, onUpdated }) => {
  const t = useT();
  const [draft, setDraft] = useState(campaign);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [stepLang, setStepLang] = useState({});
  const [preview, setPreview] = useState(null);
  const [showTest, setShowTest] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testCfg, setTestCfg] = useState({ step: 1, lang: "en" });

  useEffect(() => { setDraft(campaign); }, [campaign]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(campaign);
  const getLang = (step) => stepLang[step] || "en";

  const updateEmail = (newEmail) => {
    setDraft({
      ...draft,
      emails: (draft.emails || []).map((e) => (e.step === newEmail.step ? newEmail : e)),
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/email-campaigns/${campaign.key}`, {
        emails: draft.emails,
        enabled: draft.enabled,
      });
      toast.success(t("admin.email.toast.saved"));
      onUpdated?.();
    } catch { toast.error(t("admin.tasks.toast.save_failed")); }
    finally { setBusy(false); }
  };

  const toggleEnabled = async () => {
    setBusy(true);
    try {
      const next = !draft.enabled;
      await api.patch(`/admin/email-campaigns/${campaign.key}`, { enabled: next });
      setDraft((d) => ({ ...d, enabled: next }));
      toast.success(next ? t("admin.email.toast.enabled") : t("admin.email.toast.disabled"));
      onUpdated?.();
    } catch { toast.error(t("admin.toast.failed")); }
    finally { setBusy(false); }
  };

  const renderPreview = async (step, lang) => {
    try {
      if (dirty) {
        await api.patch(`/admin/email-campaigns/${campaign.key}`, { emails: draft.emails });
        onUpdated?.();
      }
      const { data } = await api.post(`/admin/email-campaigns/${campaign.key}/preview`, { lang, step });
      setPreview(data);
    } catch { toast.error(t("admin.sms.toast.preview_failed")); }
  };

  const onSendTestRequest = (step, lang) => {
    setTestCfg({ step, lang });
    setShowTest(true);
  };

  const doSendTest = async () => {
    if (!testTo.trim()) { toast.error(t("admin.email.toast.enter_email")); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/email-campaigns/${campaign.key}/send-test`, {
        to: testTo.trim(), step: testCfg.step, lang: testCfg.lang,
      });
      toast.success(data.mocked
        ? `${t("admin.email.toast.test_mocked")} "${data.subject}"`
        : `${t("admin.email.toast.test_sent_prefix")} ${data.to} — "${data.subject}"`);
      setShowTest(false); setTestTo("");
      onUpdated?.();
    } catch { toast.error(t("admin.sms.toast.send_failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-[#E5E5E5] bg-white p-5" data-testid={`email-camp-${campaign.key}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-serif-display text-xl">{campaign.name}</h3>
            {draft.enabled ? (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white bg-[#15803D] px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> {t("admin.sms.live")}</span>
            ) : (
              <span className="text-[10px] uppercase tracking-widest text-[#737373] border border-[#E5E5E5] px-1.5 py-0.5">{t("admin.sms.disabled")}</span>
            )}
            {campaign.kind === "series" && (
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#8B1C22] border border-[#8B1C22] px-1.5 py-0.5"><Layers className="w-3 h-3" /> {t("admin.email.series")} · {(campaign.emails || []).length} {t("admin.email.steps")}</span>
            )}
            {campaign.kind === "broadcast" && (
              <span className="text-[10px] uppercase tracking-widest text-[#C4A252] border border-[#C4A252] px-1.5 py-0.5">{t("admin.email.broadcast")}</span>
            )}
          </div>
          <div className="text-sm text-[#737373] mt-1">{campaign.description}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#C4A252] mt-1 inline-flex items-center gap-1">
            <Zap className="w-3 h-3" /> {campaign.trigger_label}
          </div>
        </div>
        <button
          onClick={toggleEnabled}
          disabled={busy}
          className="text-[#0A0A0A] hover:text-[#8B1C22] transition flex-shrink-0"
          title={draft.enabled ? t("admin.email.toggle_disable") : t("admin.email.toggle_enable")}
          data-testid={`email-camp-toggle-${campaign.key}`}
        >
          {draft.enabled ? <ToggleRight className="w-9 h-9 text-[#15803D]" /> : <ToggleLeft className="w-9 h-9" />}
        </button>
      </div>

      <div className="mb-3">
        <div className="rn3-eyebrow mb-1.5">{t("admin.sms.placeholders")}</div>
        <div className="flex flex-wrap gap-1">
          {(campaign.placeholders || []).map((p) => <PlaceholderChip key={p} name={p} />)}
        </div>
      </div>

      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-xs uppercase tracking-widest text-[#0A0A0A] hover:text-[#8B1C22] inline-flex items-center justify-center gap-1 border border-[#E5E5E5] py-2 transition"
        data-testid={`email-camp-expand-${campaign.key}`}
      >
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        {expanded ? t("admin.email.hide_editor") : `${t("admin.email.edit_emails_prefix")} ${(campaign.emails || []).length} ${(campaign.emails || []).length > 1 ? t("admin.email.emails_plural") : t("admin.email.emails_singular")}`}
      </button>

      {expanded && (
        <div className="mt-4">
          {campaign.kind === "series" && (campaign.emails || []).length > 1 && (
            <div className="inline-flex border border-[#E5E5E5] mb-4 flex-wrap" data-testid={`email-step-tabs-${campaign.key}`}>
              {(draft.emails || []).map((e) => (
                <button
                  key={e.step}
                  onClick={() => setActiveStep(e.step)}
                  className={`px-3 py-1.5 text-xs uppercase tracking-widest transition border-r last:border-r-0 border-[#E5E5E5] ${activeStep === e.step ? "bg-[#0A0A0A] text-white" : "text-[#737373] hover:bg-[#FAFAFA]"}`}
                  data-testid={`email-step-tab-${campaign.key}-${e.step}`}
                >
                  {t("admin.email.step")} {e.step}
                  <span className="ml-1.5 text-[10px] opacity-70">D{e.delay_days}</span>
                </button>
              ))}
            </div>
          )}
          {(draft.emails || []).filter((e) => e.step === activeStep || campaign.kind === "broadcast").map((e) => (
            <EmailStepEditor
              key={e.step}
              campaign={draft}
              email={e}
              lang={getLang(e.step)}
              setLang={(l) => setStepLang((s) => ({ ...s, [e.step]: l }))}
              onChange={updateEmail}
              onPreview={renderPreview}
              onSendTest={onSendTestRequest}
              busy={busy}
            />
          ))}

          {preview && (
            <div className="mt-4 border border-dashed border-[#C4A252] bg-[#FDFBF7]" data-testid={`email-preview-pane-${campaign.key}`}>
              <div className="px-3 py-2 border-b border-[#C4A252]/40 flex items-center justify-between gap-2">
                <div className="rn3-eyebrow"><Eye className="w-3 h-3 inline mr-1" /> {t("admin.sms.preview")} · {t("admin.email.step")} {preview.step} · {preview.lang === "es" ? "Español" : t("common.english")}</div>
                <button onClick={() => setPreview(null)} className="text-[10px] text-[#737373] hover:text-[#8B1C22] uppercase tracking-widest">{t("admin.email.close")}</button>
              </div>
              <div className="px-3 py-2 border-b border-[#E5E5E5] bg-white">
                <div className="text-[10px] uppercase tracking-widest text-[#737373]">{t("admin.email.subject")}</div>
                <div className="text-sm font-semibold mt-0.5">{preview.subject}</div>
              </div>
              <div className="p-4 bg-white text-sm prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: preview.body_html }} />
            </div>
          )}

          {showTest && (
            <div className="mt-4 border border-[#E5E5E5] bg-[#FAFAFA] p-3" data-testid={`email-test-form-${campaign.key}`}>
              <div className="text-xs uppercase tracking-widest text-[#737373] mb-2">{t("admin.sms.send_test")} · {t("admin.email.step")} {testCfg.step} · {testCfg.lang.toUpperCase()}</div>
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder="you@yourdomain.com"
                  className="rn3-input flex-1 text-sm"
                  data-testid={`email-test-to-${campaign.key}`}
                />
                <button onClick={doSendTest} disabled={busy || !testTo.trim()} className="rn3-btn-gold text-xs" data-testid={`email-test-send-${campaign.key}`}>
                  <Send className="w-3.5 h-3.5" /> {t("admin.sms.send")}
                </button>
                <button onClick={() => setShowTest(false)} className="text-xs text-[#737373] hover:text-[#0A0A0A] px-2">{t("common.cancel")}</button>
              </div>
              {!resendConfigured && (
                <div className="text-[10px] text-[#737373] mt-1.5">
                  <AlertCircle className="w-3 h-3 inline mr-1 text-[#C4A252]" /> {t("admin.email.resend_not_configured")}
                </div>
              )}
            </div>
          )}

          <div className="flex items-center justify-end pt-3 mt-3 border-t border-[#F3EFE9]">
            <button onClick={save} disabled={busy || !dirty} className="rn3-btn-primary text-xs disabled:opacity-40" data-testid={`email-camp-save-${campaign.key}`}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {t("admin.email.save_all")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const EmailCampaignsTab = () => {
  const t = useT();
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [resend, setResend] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/email-campaigns");
      setItems(data.items); setResend(data.resend_configured);
    } catch { toast.error(t("admin.email.toast.load_failed")); }
    finally { setLoading(false); }
  }, [t]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { nav("/portal"); return; }
    load();
  }, [user, authLoading, nav, load]);

  const enabledCount = items.filter((i) => i.enabled).length;
  const totalSteps = items.reduce((s, i) => s + ((i.emails || []).length), 0);
  const totalRuns = items.reduce((s, i) => s + (i.runs_count || 0), 0);

  return (
    <div className="rn3-container py-10" data-testid="email-campaigns-tab">
      <button onClick={() => nav("/admin")} className="text-xs uppercase tracking-widest text-[#737373] hover:text-[#8B1C22] inline-flex items-center gap-1 mb-4" data-testid="email-back">
        <ArrowLeft className="w-3 h-3" /> {t("admin.back_to_admin")}
      </button>

      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="rn3-eyebrow"><Mail className="w-3 h-3 inline mr-1" /> {t("admin.email.eyebrow")}</div>
          <h1 className="font-serif-display text-4xl mt-2">{t("admin.email.heading")}</h1>
        </div>
        <div>
          {resend ? (
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#15803D] border border-[#15803D] px-2 py-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> {t("admin.email.resend_live")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#C4A252] border border-[#C4A252] px-2 py-1" data-testid="email-mocked-banner">
              <AlertCircle className="w-3.5 h-3.5" /> {t("admin.email.resend_mocked")}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8">
        <StatCard label={t("admin.email.stat.campaigns")} value={items.length} testid="stat-email-total" />
        <StatCard label={t("admin.sms.stat.enabled")} value={enabledCount} accent={enabledCount > 0 ? "text-[#15803D]" : ""} testid="stat-email-enabled" />
        <StatCard label={t("admin.email.stat.templates")} value={totalSteps} testid="stat-email-steps" />
        <StatCard label={t("admin.sms.stat.runs")} value={totalRuns} testid="stat-email-runs" />
      </div>

      {loading && (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#C4A252]" /></div>
      )}
      {!loading && (
        <div className="grid lg:grid-cols-2 gap-4 mt-8">
          {items.map((c) => (
            <EmailCampaignCard key={c.key} campaign={c} resendConfigured={resend} onUpdated={load} />
          ))}
        </div>
      )}
    </div>
  );
};

export default EmailCampaignsTab;
